<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType, isPrivate } =
  useMessageContext();

const groupSenderName = computed(
  () => contentAttributes.value?.group_sender_name || null
);

const managementState = computed(() => {
  if (!isPrivate?.value) return null;
  const name = contentAttributes.value?.jabvoxManagementStateName;
  const color = contentAttributes.value?.jabvoxManagementStateColor;
  if (!name) return null;
  return { name, color: color || '#6366f1' };
});

// Origen de la gestión (desde dónde se tipificó) + tipificación, para mostrarlos
// en el timeline del chat igual que en el detalle del lead.
const SOURCE_LABEL_KEYS = {
  dialer: 'JABVOX_LEADS.DETAIL.SOURCE_DIALER',
  click_to_call: 'JABVOX_LEADS.DETAIL.SOURCE_CLICK_TO_CALL',
  lead_detail: 'JABVOX_LEADS.DETAIL.SOURCE_LEAD_DETAIL',
  kanban: 'JABVOX_LEADS.DETAIL.SOURCE_KANBAN',
  conversations: 'JABVOX_LEADS.DETAIL.SOURCE_CONVERSATIONS',
};
const managementSourceKey = computed(() => {
  if (!isPrivate?.value) return null;
  const src =
    contentAttributes.value?.jabvoxCallSource ||
    contentAttributes.value?.jabvox_call_source;
  return src ? SOURCE_LABEL_KEYS[src] : null;
});
const managementTypification = computed(
  () =>
    (isPrivate?.value &&
      (contentAttributes.value?.jabvoxTypificationName ||
        contentAttributes.value?.jabvox_typification_name)) ||
    null
);

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span
        v-if="groupSenderName"
        class="text-xs font-semibold text-woot-500 border-l-2 border-woot-400 pl-2 -ml-1"
      >
        {{ groupSenderName }}
      </span>
      <span
        v-if="managementState"
        class="inline-flex items-center gap-1.5 self-start text-xs font-semibold px-2 py-0.5 rounded-full"
        :style="{
          backgroundColor: managementState.color + '22',
          color: managementState.color,
          border: '1px solid ' + managementState.color + '55',
        }"
      >
        <span
          class="w-2 h-2 rounded-full flex-shrink-0"
          :style="{ backgroundColor: managementState.color }"
        />
        {{ managementState.name }}
      </span>
      <!-- Origen (desde dónde se tipificó) + tipificación -->
      <div
        v-if="managementSourceKey || managementTypification"
        class="flex flex-wrap gap-1 self-start"
      >
        <span
          v-if="managementTypification"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-n-teal-3 text-n-teal-11 border border-n-teal-6"
        >
          <span class="i-lucide-tag size-3" />
          {{ managementTypification }}
        </span>
        <span
          v-if="managementSourceKey"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-n-alpha-2 text-n-slate-11 border border-n-weak"
        >
          <span class="i-lucide-phone-outgoing size-3" />
          {{ $t(managementSourceKey) }}
        </span>
      </div>
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
